import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import { Helmet } from 'react-helmet-async'
import * as Yup from 'yup'
import { Formik } from 'formik'

import {
  Avatar,
  CardContent,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  Container,
  Box,
  Link,
  Grid,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material'

import MainCard, { StyledTabs } from '../../utils/ui_utils'
import {
  useAdminLogin,
  useBusinessLogin,
  useFOwnerLogin,
  useLogin
} from '../../modules/user/userHooks'

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;
  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(10)}px;
  }
`

const BigAvatar = styled(Avatar)`
  width: 92px;
  height: 92px;
  text-align: center;
  margin: 0 auto ${(props) => props.theme.spacing(5)}px;
`

function SignIn() {
  const { loginUser, token, loading, error } = useLogin()
  const {
    loginAdminUser,
    token: adminLoginToken,
    loading: adminLoginLoading,
    error: adminLoginError
  } = useAdminLogin()
  const { businessLoginUser } = useBusinessLogin()
  const history = useNavigate()
  const [errorMsg, setErrorMsg] = useState(null)
  const [userType, setUserType] = useState('owner')
  const {
    loginFOwner,
    loading: fOwnerLoginLoading,
    error: fOwnerLoginError
  } = useFOwnerLogin()
  const handleTabChange = (event, newAlignment) => {
    setUserType(newAlignment)
  }

  if (loading || adminLoginLoading || fOwnerLoginLoading)
    return (
      <Grid container>
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          display={'flex'}
          pt={80}
          justifyContent={'center'}
        >
          <CircularProgress size={70} />
        </Grid>
      </Grid>
    )

  return (
    <>
      {/* <Wrapper> */}
      <Helmet title="Sign In" />
      {/* <BigAvatar alt="Lucy" src="/static/img/avatars/user-512.jpg" /> */}
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <MainCard content={false}>
            {/* <Card raised sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}> */}
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                gutterBottom
              >
                Welcome back!
              </Typography>
              <Typography
                gutterBottom
                component="h2"
                variant="body1"
                align="center"
              >
                Sign in to your account to continue
              </Typography>
              <Box sx={{ mb: 2, mt: 3 }}>
                <StyledTabs
                  color="primary"
                  value={userType}
                  exclusive="true"
                  sx={{
                    marginBottom: 2
                  }}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons={false}
                  aria-label="scrollable prevent tabs example"
                >
                  <Tab
                    disabled={
                      loading || adminLoginLoading || fOwnerLoginLoading
                    }
                    sx={{
                      width: '33.33%',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px 0 0 8px'
                    }}
                    label="Owner"
                    value="owner"
                  />
                  <Tab
                    disabled={
                      loading || adminLoginLoading || fOwnerLoginLoading
                    }
                    sx={{
                      width: '33.33%',
                      border: '1px solid #e0e0e0',
                      borderRadius: '0px 8px 8px 0px'
                    }}
                    label="Admin"
                    value="admin"
                  />
                  <Tab
                    disabled={
                      loading || adminLoginLoading || fOwnerLoginLoading
                    }
                    sx={{
                      width: '33.33%',
                      border: '1px solid #e0e0e0',
                      borderRadius: '0px 8px 8px 0px'
                    }}
                    label="FOwner"
                    value="fowner"
                  />
                </StyledTabs>
              </Box>
              <Formik
                initialValues={{
                  loginID: '',
                  password: '',
                  business_code: '',
                  submit: false
                }}
                validationSchema={Yup.object().shape({
                  loginID: Yup.string().required('Login Id is required'),
                  password: Yup.string()
                    .max(255)
                    .required('Password is required'),
                  business_code:
                    ((userType === 'admin' || userType === 'fowner') &&
                      Yup.string()
                        .required('Business code is required')
                        .min(10, 'Business code must be 10 characters')
                        .max(
                          15,
                          'Business code must less than 15 characters'
                        )) ||
                    Yup.string().notRequired()
                })}
                onSubmit={async (
                  values,
                  { setErrors, setStatus, setSubmitting }
                ) => {
                  try {
                    let payload = {
                      user: values.loginID,
                      password: values.password,
                      business_code: values.business_code
                    }

                    if (userType === 'admin' && payload.business_code) {
                      await loginAdminUser(
                        payload,
                        async (success, error, res) => {
                          if (error) {
                            const message =
                              res.error.message || 'Something went wrong!!!!'
                            setErrorMsg(message)
                          } else {
                            const usertype = localStorage.getItem('usertype')
                            const access_token =
                              localStorage.getItem('access_token')
                            if (
                              usertype === 'Owner' ||
                              usertype === 'Admin' ||
                              usertype == 'User'
                            ) {
                              await businessLoginUser(
                                access_token,
                                async (success, error, res) => {
                                  if (success) {
                                    history('/dashboard/default')
                                    window.location.reload()
                                  } else {
                                    const message =
                                      res.error.message ||
                                      'Business Login Failed'
                                    setErrorMsg(message)
                                  }
                                }
                              )
                            } else {
                              history('/host/application')
                            }
                          }
                        }
                      )
                    } else if (userType === 'owner') {
                      delete payload.business_code
                      await loginUser(payload, async (success, error, res) => {
                        if (error) {
                          const message =
                            res.error.message || 'Something went wrong!!!!'
                          setErrorMsg(message)
                        } else {
                          const usertype = localStorage.getItem('usertype')
                          const access_token =
                            localStorage.getItem('access_token')
                          if (
                            usertype === 'Owner' ||
                            usertype === 'Admin' ||
                            usertype == 'User'
                          ) {
                            await businessLoginUser(
                              access_token,
                              async (success, error, res) => {
                                if (success) {
                                  history('/dashboard/default')
                                  window.location.reload()
                                } else {
                                  const message =
                                    res.error.message || 'Business Login Failed'
                                  setErrorMsg(message)
                                }
                              }
                            )
                          } else {
                            history('/host/application')
                          }
                        }
                      })
                    } else if (userType === 'fowner' && payload.business_code) {
                      await loginFOwner(
                        payload,
                        async (success, error, res) => {
                          if (error) {
                            const message =
                              res.error.message || 'Something went wrong!!!!'
                            setErrorMsg(message)
                          } else {
                            const usertype = localStorage.getItem('usertype')
                            const access_token =
                              localStorage.getItem('access_token')
                            if (usertype === 'FOwner') {
                              await businessLoginUser(
                                access_token,
                                async (success, error, res) => {
                                  if (success) {
                                    history('/dashboard/default')
                                    window.location.reload()
                                  } else {
                                    const message =
                                      res.error.message ||
                                      'Business Login Failed'
                                    setErrorMsg(message)
                                  }
                                }
                              )
                            } else {
                              history('/host/application')
                            }
                          }
                        }
                      )
                    }
                  } catch (err) {
                    const message =
                      userType === 'admin'
                        ? adminLoginError
                        : error || 'Something went wrong!!!!'
                    setStatus({ success: false })
                    setErrors({ submit: message })
                    setSubmitting(false)
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values
                }) => (
                  <form noValidate onSubmit={handleSubmit}>
                    {errorMsg && (
                      <Alert mt={2} mb={1} severity="warning">
                        {errorMsg}
                      </Alert>
                    )}

                    <TextField
                      margin="normal"
                      // type="text"
                      name="loginID"
                      label="Login ID"
                      value={values.loginID}
                      error={Boolean(touched.loginID && errors.loginID)}
                      fullWidth
                      helperText={touched.loginID && errors.loginID}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={2}
                    />
                    <TextField
                      margin="normal"
                      type="password"
                      name="password"
                      label="Password"
                      value={values.password}
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={2}
                    />

                    {(userType === 'admin' || userType === 'fowner') && (
                      <TextField
                        margin="normal"
                        type="text"
                        name="business_code"
                        label="Business code"
                        value={values.business_code}
                        error={Boolean(
                          touched.business_code && errors.business_code
                        )}
                        fullWidth
                        helperText={
                          touched.business_code && errors.business_code
                        }
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={2}
                      />
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      sx={{ mt: 3, mb: 2 }}
                    >
                      Sign in
                    </Button>
                    <Grid container sx={{ justifyContent: 'space-between' }}>
                      <Grid item>
                        <Link href="/auth/reset-password">Forgot password</Link>
                      </Grid>
                      <Grid item>
                        <Link href="/auth/sign-up">Sign Up</Link>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            </CardContent>
          </MainCard>
        </Box>
      </Container>
      {/* </Wrapper> */}
    </>
  )
}

export default SignIn
